import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Formik, FormikHelpers } from 'formik';
import {
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Theme,
} from '@material-ui/core';
import Button from '@material-ui/core/Button/Button';
import Typography from '@material-ui/core/Typography/Typography';

import { UserInfo } from 'types';
import { PageKey } from 'enums';
import {
  BreadcrumbsCtx,
  initialBreadcrumbs,
} from 'components/portfolio/breadcrumbs-ctx';
import { AuthCtx } from 'components/auth/auth-ctx';
import { FormItemTextField } from 'components/form-item/form-item-text-field';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { Loader } from 'components/loader/loader';

import { postUser, syncWhoIs } from './user-profile-api';
import { validationSchema } from './user-profile-validation';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      justifyContent: 'center',
    },
    buttons: {
      display: 'flex',
    },
    submitButton: {
      margin: '15px 0',
      marginRight: '15px',
    },
    dialog: {
      width: 500,
    },
    dialogTitle: {
      fontWeight: 'bold',
    },
    dialogActions: {
      marginTop: 20,
      padding: '8px 24px',
    },
  })
);

type UserDataType = {
  key:
    | 'firstName'
    | 'lastName'
    | 'birthDate'
    | 'birthPlace'
    | 'casId'
    | 'email'
    | 'phone'
    | 'permanentResidence.municipality'
    | 'permanentResidence.street'
    | 'permanentResidence.zip'
    | 'deliveryAddress.municipality'
    | 'deliveryAddress.street'
    | 'deliveryAddress.zip';
  defaultMessage: string;
  type?: string;
  disabled?: boolean;
};

export function UserProfile() {
  const classes = useStyles();
  const { setBreadcrumbs } = useContext(BreadcrumbsCtx);
  const { userInfo, refreshUser } = useContext(AuthCtx);
  const { showNotification } = useContext(NotificationCtx);
  const [syncLoading, setSyncLoading] = useState(false);
  const [openedDialog, setOpenedDialog] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const [isEditing, setIsEditing] = useState(false);

  const handleOpenDialog = () => setOpenedDialog(true);
  const handleCloseDialog = () => setOpenedDialog(false);

  const userData: UserDataType[] = useMemo(() => {
    return [
      { key: 'firstName', defaultMessage: 'Jméno' },
      { key: 'lastName', defaultMessage: 'Příjmení' },
      { key: 'birthDate', defaultMessage: 'Datum narození', type: 'date' },
      { key: 'birthPlace', defaultMessage: 'Místo narození' },
      { key: 'casId', defaultMessage: 'Číslo CAS' },
      { key: 'email', defaultMessage: 'Email', disabled: false },
      {
        key: 'phone',
        defaultMessage: 'Telefon',
        disabled: false,
      },
      {
        key: 'permanentResidence.municipality',
        defaultMessage: 'Obec (adresa trvalého bydliště)',
        disabled: false,
      },
      {
        key: 'permanentResidence.street',
        defaultMessage: 'Ulice (adresa trvalého bydliště)',
        disabled: false,
      },
      {
        key: 'permanentResidence.zip',
        defaultMessage: 'PSČ (adresa trvalého bydliště)',
        disabled: false,
      },
      {
        key: 'deliveryAddress.municipality',
        defaultMessage: 'Obec (adresa pro doručení)',
        disabled: false,
      },
      {
        key: 'deliveryAddress.street',
        defaultMessage: 'Ulice (adresa pro doručení)',
        disabled: false,
      },
      {
        key: 'deliveryAddress.zip',
        defaultMessage: 'PSČ (adresa pro doručení)',
        disabled: false,
      },
    ];
  }, []);

  const handleSubmit = useCallback(
    async (user: UserInfo, actions: FormikHelpers<UserInfo>) => {
      try {
        const response = await postUser(user);

        if (response) {
          await refreshUser();

          showNotification(
            'Profil úspěšně upraven',
            NotificationVariant.SUCCESS
          );
        } else {
          throw new Error('error');
        }
      } catch (e) {
        showNotification(
          'Při úpravě profilu došlo k chybě',
          NotificationVariant.ERROR
        );
      } finally {
        actions.setSubmitting(false);
        setIsEditing(false);
      }
    },
    [refreshUser, showNotification]
  );

  const handleSync = useCallback(async () => {
    try {
      setSyncLoading(true);
      const response = await syncWhoIs();

      if (response) {
        await refreshUser();

        showNotification(
          'Data úspěšně synchronizováná',
          NotificationVariant.SUCCESS
        );
        setOpenedDialog(false);
      } else {
        throw new Error('error');
      }
    } catch (e) {
      showNotification(
        'Při synchronizaci došlo k chybě',
        NotificationVariant.ERROR
      );
    } finally {
      setSyncLoading(false);
    }
  }, [refreshUser, showNotification]);

  useEffect(() => {
    setBreadcrumbs([
      ...initialBreadcrumbs.breadcrumbs,
      {
        title: 'Profil',
        key: PageKey.PORTFOLIO_PROFILE,
      },
    ]);
  }, [setBreadcrumbs]);

  return (
    <>
      <Formik<UserInfo>
        initialValues={userInfo!}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
        validateOnMount={false}
        validationSchema={validationSchema}
      >
        {(props) => (
          <form onSubmit={props.handleSubmit}>
            {(props.isSubmitting || syncLoading) && <Loader />}
            <Grid container className={classes.container}>
              <Grid item xs={9} md={6}>
                {userData.map(
                  ({ key, defaultMessage, type, disabled = true }) => (
                    <FormItemTextField
                      name={key}
                      type={type}
                      label={formatMessage({
                        id: `portfolioProfil.${key}`,
                        defaultMessage,
                      })}
                      disabled={disabled || !isEditing}
                    />
                  )
                )}
                <div className={classes.buttons}>
                  <Button
                    className={classes.submitButton}
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!isEditing && props.isSubmitting}
                    style={{
                      display: isEditing ? 'inherit' : 'none',
                    }}
                  >
                    <Typography component="span" variant="body2">
                      Uložit změny
                    </Typography>
                  </Button>
                  <Button
                    className={classes.submitButton}
                    variant="contained"
                    color="primary"
                    onClick={() => setIsEditing(true)}
                    style={{
                      display: isEditing ? 'none' : 'inherit',
                    }}
                  >
                    <Typography component="span" variant="body2">
                      Upravit
                    </Typography>
                  </Button>
                  <Button
                    className={classes.submitButton}
                    variant="contained"
                    onClick={handleOpenDialog}
                    style={{
                      display: isEditing ? 'none' : 'inherit',
                    }}
                  >
                    <Typography component="span" variant="body2">
                      Synchronizovat z CAS
                    </Typography>
                  </Button>
                </div>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>

      <Dialog open={openedDialog}>
        <Grid className={classes.dialog}>
          <DialogTitle>
            <Typography component="span" className={classes.dialogTitle}>
              Synchronizace z CAS
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Údaje uživatele budou nahrazeny údaji z Centrální autentizační
              služby UK, skutečně chcete údaje nahradit údaji z CAS?
            </Typography>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button color="primary" onClick={handleSync}>
              Potvrdit
            </Button>
            <Button onClick={handleCloseDialog}>Zavřít</Button>
          </DialogActions>
        </Grid>
      </Dialog>
    </>
  );
}
